import React, {ChangeEvent, MutableRefObject, useEffect, useRef, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import classNames from 'classnames';
import {History} from 'history';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {TabContext, TabPanel} from '@material-ui/lab';
import {AppBar, Tab, Tabs, CircularProgress} from '@material-ui/core';
import {ApTimelineBasic} from '@accurate-player/accurate-player-components-external';
import useQuery from '@pluto/query-hook';
import {formQuery} from '@pluto/rest';
import VideoAudioQC from './VideoAudioQC/VideoAudioQCConnected';
import ContentItemViewMetadata from './ContentItemViewMetadata/ContentItemViewMetadataConnected';
import ContentItemViewPublishingRightsContainer from './ContentItemViewPublishingRights/ContentItemViewPublishingRightsConnected';
import ContentItemViewArtwork from './ContentItemViewArtwork/ContentItemViewArtworkConnected';
import Divider from './Divider';
import {handleFetchQcMetadata} from './ContentItemViewMetadata/ContentItemViewMetadataContainer';
import {HandleChange, SetCurrentTab, TabsEnum} from './ContentItemViewTypes';
import {QcJobStatusIndicatorStateItem} from './JobStatusIndicator/JobStatusIndicatorTypes';
import {
    Item,
    ResetItemAction,
    SetItemAction,
} from './VideoAudioQC/QCMedia/Redux/QCMediaActions';
import {
    PlayerRef,
    ProgressivePlayerOrNull,
} from './VideoAudioQC/AccuratePlayer/AccuratePlayerTypes';
import QCTitleContainer from './ContentItemViewTitles/ContentItemViewTitlesContainer';
import ContentItemViewWorkHistoryContainer from './ContentItemViewWorkHistory/ContentItemViewWorkHistoryContainer';
import './ContentItemView.scss';

export interface HandleChangeParams {
    setCurrentTab: SetCurrentTab;
    playerRef: PlayerRef;
    history: History;
    id: string | null;
    title: string | null;
    isChangingTabAllowedRef: MutableRefObject<boolean>;
    currentTab: TabsEnum | string;
}

export interface ContentItemViewContainerProps {
    item: Item | null;
    resetItem: ResetItemAction;
    setQcJobState: (id: number) => void;
    statusIndicators: QcJobStatusIndicatorStateItem[];
    setQcMetadata: (id: number) => void;
    resetQcMetadata: () => void;
    resetQcJobState: () => void;
    isLoading: boolean;
    isMetadataLoading: boolean;
    contentType: string | undefined;
    deliveryStartedAt: string;
    isDeliveryStarted: boolean;
    resetJobDeliveryStatus: () => void;
    getDeliveryJobStatus: (qcJobId: number) => void;
    deliveryJob: (qcJobId: number) => void;
    setItem: SetItemAction;
}

export const getStatusByType = (
    statusIndicators: QcJobStatusIndicatorStateItem[],
    type: string
) => {
    if (!statusIndicators) return null;

    const statusValue = statusIndicators.filter(
        (status: QcJobStatusIndicatorStateItem) => status.type === type
    );

    return statusValue[0];
};

export const handleChange = curry(
    (params: HandleChangeParams, event: ChangeEvent<{}>, newValue: TabsEnum) => {
        const {
            setCurrentTab,
            history,
            isChangingTabAllowedRef,
            playerRef,
            currentTab,
        } = params;
        const queryParams = {
            tab: newValue,
        };
        const search = formQuery('', queryParams);

        if (isChangingTabAllowedRef.current) {
            if (playerRef?.current && currentTab === TabsEnum.visualQc) {
                playerRef.current.api.pause();
            }

            setCurrentTab(newValue);
            history.push({
                pathname: history.location.pathname,
                search,
            });
        }
    }
);

export const getInitialTabValue = (tabValue: TabsEnum | undefined): TabsEnum | string =>
    tabValue || TabsEnum.none;

export const handleUnmount = wrap((params: any) => {
    const {resetQcMetadata, resetQcJobState, resetJobDeliveryStatus, resetItem} = params;
    resetQcMetadata();
    resetQcJobState();
    resetJobDeliveryStatus();
    resetItem();
});

export const fetchData = wrap((params: any) => {
    const {onHandleUnmountEffect, fetchMetadata, setItem, id} = params;
    fetchMetadata();
    setItem(id);

    return onHandleUnmountEffect;
});

export const onChangingTabAllowedRefEffect = wrap(
    (isChangingTabAllowedRef: MutableRefObject<boolean>) => {
        isChangingTabAllowedRef.current = true;
    }
);

export const handleSettingCurrentTab = wrap(
    (
        contentType: string | undefined,
        setCurrentTab: (tab: TabsEnum) => void,
        tabValue: TabsEnum
    ) => {
        if (contentType) {
            if (contentType !== 'series') {
                setCurrentTab(tabValue || TabsEnum.visualQc);
            } else {
                setCurrentTab(tabValue || TabsEnum.metadata);
            }
        }
    }
);

export const handleDeliveryJob = wrap(
    (deliveryJob: (qcJobId: string) => void, qcJobId: string) => {
        deliveryJob(qcJobId);
    }
);

export default function ContentItemViewContainer(props: ContentItemViewContainerProps) {
    const {
        setQcMetadata,
        resetQcMetadata,
        isMetadataLoading,
        contentType,
        deliveryStartedAt: jobDeliveryDate,
        resetJobDeliveryStatus,
        resetQcJobState,
        setItem,
        resetItem,
        item,
    } = props;

    const isChangingTabAllowedRef = useRef<boolean>(false);
    const reloadRef = useRef<boolean>(false);
    const {id} = useParams<{id: string}>();

    const {title} = useParams<{title: string}>();
    const fetchMetadata = handleFetchQcMetadata(setQcMetadata, id, isMetadataLoading);
    const onHandleUnmountEffect = handleUnmount({
        resetQcMetadata,
        resetQcJobState,
        resetJobDeliveryStatus,
        resetItem,
    });

    useEffect(
        fetchData({
            fetchMetadata,
            onHandleUnmountEffect,
            setItem,
            id,
        }),
        []
    );
    useEffect(fetchMetadata, [isMetadataLoading]);

    useEffect(onChangingTabAllowedRefEffect(isChangingTabAllowedRef), []);

    const history = useHistory();

    const query = useQuery();
    const tabValue = query.get('tab') as TabsEnum;
    const initialTabValue = getInitialTabValue(tabValue);
    const [currentTab, setCurrentTab] = useState<TabsEnum | string>(initialTabValue);
    const playerRef = useRef<ProgressivePlayerOrNull>(null);
    const timelineRef = useRef<ApTimelineBasic | null>(null);
    const onHandleChange: HandleChange = handleChange({
        playerRef,
        setCurrentTab,
        currentTab,
        history,
        id,
        title,
        isChangingTabAllowedRef,
    });

    useEffect(handleSettingCurrentTab(contentType, setCurrentTab, tabValue), [
        contentType,
        tabValue,
    ]);

    const isVisualQcCurrentTab = currentTab === TabsEnum.visualQc;

    if (!contentType) {
        return <CircularProgress />;
    }

    return (
        <div className="tools-container">
            <AppBar position="absolute" className="qc-app-bar">
                <Tabs value={currentTab} onChange={onHandleChange}>
                    {contentType !== 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.visualQc}
                            value={TabsEnum.visualQc}
                        />
                    )}
                    <Tab className="qc-tools__tab hidden" value={TabsEnum.none} />
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.metadata}
                        value={TabsEnum.metadata}
                    />
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.artwork}
                        value={TabsEnum.artwork}
                    />
                    <Divider />
                    {contentType !== 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.rights}
                            value={TabsEnum.rights}
                        />
                    )}
                    {contentType === 'series' && (
                        <Tab
                            className="qc-tools__tab"
                            label={TabsEnum.titles}
                            value={TabsEnum.titles}
                        />
                    )}
                    <Tab
                        className="qc-tools__tab"
                        label={TabsEnum.workHistory.replace('_', ' ')}
                        value={TabsEnum.workHistory}
                    />
                </Tabs>
            </AppBar>
            <div className="qc-tools">
                <TabContext value={currentTab}>
                    <>
                        {contentType !== 'series' && (
                            <div
                                className={classNames('video-audio-qc-tab-wrapper', {
                                    hidden: !isVisualQcCurrentTab,
                                })}
                            >
                                <VideoAudioQC
                                    playerRef={playerRef}
                                    timelineRef={timelineRef}
                                    item={item}
                                    routeId={id}
                                    jobDeliveryDate={jobDeliveryDate}
                                />
                            </div>
                        )}

                        <TabPanel value={TabsEnum.workHistory}>
                            <ContentItemViewWorkHistoryContainer id={id} />
                        </TabPanel>

                        {contentType !== 'series' && (
                            <TabPanel value={TabsEnum.rights}>
                                <ContentItemViewPublishingRightsContainer />
                            </TabPanel>
                        )}
                    </>
                    <TabPanel value={TabsEnum.none} />
                    <TabPanel value={TabsEnum.metadata}>
                        <ContentItemViewMetadata
                            jobDeliveryDate={jobDeliveryDate}
                            reloadRef={reloadRef}
                        />
                    </TabPanel>
                    <TabPanel value={TabsEnum.artwork}>
                        <ContentItemViewArtwork jobDeliveryDate={jobDeliveryDate} />
                    </TabPanel>
                    {contentType === 'series' && (
                        <TabPanel value={TabsEnum.titles}>
                            <QCTitleContainer id={id} />
                        </TabPanel>
                    )}
                </TabContext>
            </div>
        </div>
    );
}
